import { CalendarIcon } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

/** "YYYY-MM-DD" z lokálního data, bez převodu do UTC (jinak by se půlnoc posunula na předchozí den). */
export const toIsoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

/** Lokální datum z "YYYY-MM-DD", nebo undefined. */
export const fromIsoDate = (value: string | undefined | null): Date | undefined => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '')
  if (!m) return undefined
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return date.getMonth() === Number(m[2]) - 1 ? date : undefined
}

/** Česky zapsané datum ("5. 11. 2026", "5.11.2026", "5.11.") nebo "2026-11-05". Bez roku se doplní letošní. */
export const parseTypedDate = (text: string): Date | undefined => {
  const t = text.trim()
  if (!t) return undefined
  const iso = fromIsoDate(t)
  if (iso) return iso
  const m = /^(\d{1,2})\s*\.\s*(\d{1,2})\s*\.?\s*(\d{2}|\d{4})?$/.exec(t)
  if (!m) return undefined
  const year = m[3] ? (m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])) : new Date().getFullYear()
  const date = new Date(year, Number(m[2]) - 1, Number(m[1]))
  return date.getMonth() === Number(m[2]) - 1 && date.getDate() === Number(m[1]) ? date : undefined
}

const formatCz = (date: Date) => `${date.getDate()}. ${date.getMonth() + 1}. ${date.getFullYear()}`

type DatePickerProps = {
  /** "YYYY-MM-DD" nebo prázdný řetězec */
  value: string | undefined | null
  onChange: (value: string) => void
  onBlur?: () => void
  id?: string
  name?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

/**
 * Pole pro datum: datum jde napsat česky, nebo vybrat v našem kalendáři (ikona vpravo, šipka dolů).
 * Nahrazuje systémové <input type="date">, které vypadá v každém prohlížeči jinak a je anglicky.
 */
export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  ({ value, onChange, onBlur, id, name, placeholder = 'd. m. rrrr', disabled, className, ...aria }, ref) => {
    const selected = fromIsoDate(value)
    const [text, setText] = React.useState(selected ? formatCz(selected) : '')
    const [open, setOpen] = React.useState(false)
    const [month, setMonth] = React.useState<Date | undefined>(selected)

    // Hodnota změněná zvenku (reset formuláře, výchozí datum) přepíše text v poli.
    React.useEffect(() => {
      const date = fromIsoDate(value)
      setText(date ? formatCz(date) : '')
      if (date) setMonth(date)
    }, [value])

    const commit = () => {
      if (!text.trim()) {
        if (value) onChange('')
        return
      }
      const date = parseTypedDate(text)
      if (date) {
        setText(formatCz(date))
        setMonth(date)
        if (toIsoDate(date) !== value) onChange(toIsoDate(date))
      } else {
        // Nesrozumitelný zápis vrátí poslední platné datum.
        setText(selected ? formatCz(selected) : '')
      }
    }

    return (
      <div className={cn('relative', className)}>
        <Input
          ref={ref}
          id={id}
          name={name}
          value={text}
          placeholder={placeholder}
          disabled={disabled}
          inputMode="numeric"
          autoComplete="off"
          className="pr-10 tabular"
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            commit()
            onBlur?.()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commit()
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setOpen(true)
            }
          }}
          {...aria}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled}
              className="absolute top-1/2 right-1 size-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Vybrat datum v kalendáři"
            >
              <CalendarIcon className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0" align="end" sideOffset={8}>
            <Calendar
              mode="single"
              captionLayout="dropdown"
              selected={selected}
              // Doklady i předplatné: 15 let zpět, 5 let dopředu (výchozí rozbalovací rok kalendáře je užší).
              startMonth={new Date(new Date().getFullYear() - 15, 0)}
              endMonth={new Date(new Date().getFullYear() + 5, 11)}
              month={month}
              onMonthChange={setMonth}
              onSelect={(date) => {
                if (date) {
                  setText(formatCz(date))
                  onChange(toIsoDate(date))
                }
                setOpen(false)
              }}
              autoFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    )
  },
)
DatePicker.displayName = 'DatePicker'
